import { useEffect, useState } from "react";
import { Link } from "react-router";
import { PLAN_LABEL, PLAN_PERKS, SELLER_PLANS } from "../../shared/market";
import { ONBOARDING_BIO_CHARS, ONBOARDING_LISTINGS, onboardingView, type OnboardingTask, type OnboardingTaskKey } from "../../shared/onboarding";
import { HOME_TASKS, type HomeMessage, type HomeOrder, type SellerTask, type SellerTaskKind } from "../../shared/marketStats";
import { orderPath, sellerPath, type SellerSection } from "../../shared/sellerPaths";
import { ErrorMessage, Loading } from "../components/StatusMessage";
import { useI18n } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/messages";
import { actingShop } from "../lib/actAs";
import { useAsync } from "../lib/useAsync";
import { marketApi, type MySeller } from "./api";
import { useArs } from "./components";
import { sellerNextStep } from "./orderStep";
import { noteUpgrade, ShowcaseCard } from "./Showcase";

/** Where Inicio's buttons lead: a section of the panel and, for Publicaciones, what to open. */
export type Go = (to: SellerSection, state?: { add: "one" | "sheet" }) => void;

/**
 * Mi tienda › Inicio, top to bottom: what to do today, quick actions, the week, the shop, what could
 * sell more, the last sales and messages, and the plan in one line. A block with nothing to say hides.
 */
export function SellerHome({ seller, go, onSaved }: { seller: MySeller; go: Go; onSaved: (s: MySeller) => void }) {
  const { t } = useI18n();
  const ars = useArs();
  const home = useAsync((signal) => marketApi.home(signal), []);
  // What buyers want, for this account: not the admin's own when it works from inside the shop.
  const wanted = useAsync((signal) => (actingShop() ? Promise.resolve(null) : marketApi.wantedMatches(signal)), []);
  const showcase = useAsync((signal) => marketApi.showcase(signal), []);
  const pick = showcase.data?.pick ?? null;
  const plan = seller.plan;
  const live = plan?.liveListings ?? 0;
  if (home.error) return <ErrorMessage onRetry={home.reload} />;
  if (!home.data) return <Loading />;
  const h = home.data;
  // Before the first sale the guide takes the week's place (there's nothing to count yet).
  const guiding = onboardingView(seller, { listings: live, completedSales: h.completedSales, acting: actingShop() !== null }) !== null;
  const wants = wanted.data && wanted.data.cards > 0 ? wanted.data : null;

  return (
    <div className="seller-home stack">
      <SetupGuide seller={seller} listings={live} completedSales={h.completedSales} onSaved={onSaved} />
      <TasksCard tasks={h.tasks} hidden={h.hiddenTasks} onChanged={home.reload} />
      <QuickActions seller={seller} go={go} />

      <div className="shop-home-grid">
        {!guiding && (
          <section className="plan-card shop-home-card" aria-labelledby="home-week">
            <h2 id="home-week" className="small-title">
              {t("home.week")}
            </h2>
            <p className="shop-home-big">
              {ars(h.week.salesArs)} <Trend now={h.week.salesArs} before={h.week.prevSalesArs} />
            </p>
            <p className="muted small">
              {t("home.orders", { count: h.week.orders })} · {t("home.views", { count: h.week.views })} · {t("home.followers", { count: h.week.followers })}
            </p>
            <WeeksChart weeks={h.weeks} label={t("home.weeksChart")} format={ars} />
          </section>
        )}
        <section className="plan-card shop-home-card" aria-labelledby="home-shop">
          <h2 id="home-shop" className="small-title">
            {t("home.shop")}
          </h2>
          <p className="shop-home-big">{plan?.maxListings ? t("home.liveOf", { live, max: plan.maxListings }) : t("home.live", { count: live })}</p>
          <ul className="shop-home-links">
            {h.shop.soldOut > 0 && (
              <li>
                <Link to={`${sellerPath("publicaciones")}?f=soldout`}>{t("home.soldOut", { count: h.shop.soldOut })} →</Link>
              </li>
            )}
            {h.noViews30d > 0 && (
              <li>
                <Link to={sellerPath("publicaciones")}>{t("home.noViews", { count: h.noViews30d })} →</Link>
              </li>
            )}
          </ul>
        </section>
      </div>

      {/* At most two: who's looking for the shop's cards, and one paid perk with the shop's own number. */}
      {(wants || pick) && (
        <section className="stack-sm" aria-labelledby="home-opps">
          <h2 id="home-opps" className="small-title">
            {t("home.opportunities")}
          </h2>
          {wants && (
            <Link to="/se-busca" className="small home-wanted">
              {t("wanted.matchesLocked", { people: t("wanted.people", { count: wants.wanting }), count: wants.cards })} →
            </Link>
          )}
          {pick && <ShowcaseCard showcase={pick} />}
        </section>
      )}

      <RecentCard orders={h.recentOrders} messages={h.recentMessages} />

      {plan && (
        <p className="shop-home-plan small">
          <strong>{t("home.plan", { plan: PLAN_LABEL[plan.name] })}</strong>
          {plan.maxListings !== null && ` · ${t("home.planListings", { live, max: plan.maxListings })}`}
          {plan.featuredPerMonth > 0 && ` · ${t("home.planFeatured", { count: plan.featuredLeft })}`}{" "}
          {/* One way up per screen: with a showcase, the plan line has no button. */}
          {!pick && (
            <button type="button" className="btn btn-link btn-sm" onClick={() => (noteUpgrade("linea-plan"), go("plan"))}>
              {t("home.seePlans")}
            </button>
          )}
        </p>
      )}
    </div>
  );
}

export const TASK_LABEL: Record<SellerTaskKind, MessageKey> = {
  confirm_payment: "todo.confirmPayment",
  unanswered: "todo.unanswered",
  offers: "todo.offers",
  connect_mp: "todo.connectMp",
  deliver: "todo.deliver",
  meeting: "todo.meeting",
  unpaid: "todo.unpaid",
  reviews: "todo.reviews",
  soldout: "todo.soldout",
  coupon_ending: "todo.couponEnding",
  verify: "todo.verify",
};

/** Where a task that isn't about some orders is done. */
export const TASK_PLACE: Partial<Record<SellerTaskKind, string>> = {
  offers: sellerPath("ofertas"),
  connect_mp: sellerPath("ajustes"),
  soldout: `${sellerPath("publicaciones")}?f=soldout`,
  coupon_ending: sellerPath("cupones"),
  verify: sellerPath("ajustes"),
};

/** A task as a line: what to do, with how many (a yes/no one, like connecting Mercado Pago, has no number). */
export const taskText = (t: (k: MessageKey) => string, task: SellerTask) => (task.kind === "connect_mp" || task.kind === "verify" ? t(TASK_LABEL[task.kind]) : `${t(TASK_LABEL[task.kind])} (${task.count})`);

/** Where a task leads: its place, its one order, or the sales. */
export const taskHref = (task: SellerTask) => TASK_PLACE[task.kind] ?? (task.count === 1 && task.orders.length === 1 ? orderPath(task.orders[0].number, "seller") : sellerPath("pedidos"));

/** Fired when the shop dismisses or brings back tasks, so the panel's bell re-reads them. */
export const TASKS_CHANGED = "pf-seller-tasks-changed";

/**
 * "Para hacer hoy": the most urgent first, five at a time; each one leads to where it's done. "✕" takes
 * one off (what it has now: something new brings it back); "Volver a mostrar" brings back every one.
 */
function TasksCard({ tasks: all0, hidden, onChanged }: { tasks: SellerTask[]; hidden: number; onChanged: () => void }) {
  const { t } = useI18n();
  const [all, setAll] = useState(false);
  // Off the list as soon as it's dismissed; back if the server says no.
  const [gone, setGone] = useState<ReadonlySet<SellerTaskKind>>(new Set());
  const [failed, setFailed] = useState(false);
  // Fresh from the server: it already leaves out what was dismissed.
  useEffect(() => setGone(new Set()), [all0]);
  const tasks = all0.filter((x) => !gone.has(x.kind));
  const shown = all ? tasks : tasks.slice(0, HOME_TASKS);
  const changed = () => {
    window.dispatchEvent(new Event(TASKS_CHANGED));
    onChanged();
  };
  const dismiss = async (task: SellerTask) => {
    setFailed(false);
    setGone((g) => new Set(g).add(task.kind));
    try {
      await marketApi.dismissTask(task);
      changed();
    } catch {
      setGone((g) => {
        const next = new Set(g);
        next.delete(task.kind);
        return next;
      });
      setFailed(true);
    }
  };
  const restore = async () => {
    setFailed(false);
    try {
      await marketApi.restoreTasks();
      setGone(new Set());
      changed();
    } catch {
      setFailed(true);
    }
  };
  return (
    <section className="plan-card todo-card" aria-labelledby="todo-title">
      <h2 id="todo-title" className="small-title">
        {t("todo.title")}
      </h2>
      {tasks.length === 0 ? (
        <p className="muted small">{t("todo.allDone")}</p>
      ) : (
        <ul className="todo-list">
          {shown.map((task) => {
            const place = TASK_PLACE[task.kind];
            const label = taskText(t, task);
            return (
              <li key={task.kind} className={`todo-${task.severity}`}>
                <button type="button" className="todo-dismiss" aria-label={t("todo.dismiss", { task: label })} title={t("todo.dismissHint")} onClick={() => void dismiss(task)}>
                  ✕
                </button>
                {place ? (
                  <Link to={place}>
                    <strong>{label}</strong>
                  </Link>
                ) : (
                  <strong>{label}</strong>
                )}
                {task.orders.length > 0 && (
                  <span className="small">
                    {task.orders.map((o, i) => (
                      <span key={o.number}>
                        {i > 0 && " · "}
                        <Link to={orderPath(o.number, "seller")}>{o.number}</Link> <span className="muted">{o.buyerName}</span>
                      </span>
                    ))}
                    {task.count > task.orders.length && (
                      <>
                        {" · "}
                        <Link to={sellerPath("pedidos")}>{t("todo.more", { count: task.count - task.orders.length })}</Link>
                      </>
                    )}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {tasks.length > HOME_TASKS && (
        <button type="button" className="btn btn-link btn-sm" aria-expanded={all} onClick={() => setAll(!all)}>
          {all ? t("todo.fewer") : t("todo.all", { count: tasks.length })}
        </button>
      )}
      {failed && (
        <p className="form-error" role="alert">
          {t("todo.dismissFailed")}
        </p>
      )}
      {hidden > 0 && gone.size === 0 && (
        <p className="muted small">
          {t("todo.hidden", { count: hidden })}{" "}
          <button type="button" className="btn btn-link btn-sm" onClick={() => void restore()}>
            {t("todo.restore")}
          </button>
        </p>
      )}
    </section>
  );
}

/** One row of big buttons (scrolls sideways on a phone). What the plan doesn't include shows a padlock and leads to the plans. */
function QuickActions({ seller, go }: { seller: MySeller; go: Go }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const perks = PLAN_PERKS[seller.plan?.name ?? "basic"];
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/vendedores/${seller.handle}`);
      setCopied(true);
    } catch {
      // No clipboard (an old browser, a denied permission): the link is on "Ver mi tienda".
    }
  };
  // What the plan lacks: a padlock, naming the first plan that has it.
  const gated = (perk: "reservations" | "coupons", icon: string, action: string, to: SellerSection) =>
    perks[perk] > 0 ? (
      <button type="button" className="btn btn-outline" onClick={() => go(to)}>
        {icon} {action}
      </button>
    ) : (
      <button type="button" className="btn btn-outline" title={t("qa.locked", { action, plan: PLAN_LABEL[SELLER_PLANS.find((p) => PLAN_PERKS[p][perk] > 0) ?? "vmax"] })} onClick={() => (noteUpgrade(perk === "coupons" ? "accion:cupon" : "accion:reserva"), go("plan"))}>
        🔒 {action}
      </button>
    );
  return (
    <section className="quick-actions" aria-label={t("qa.title")}>
      <button type="button" className="btn btn-primary" onClick={() => go("publicar")}>
        + {t("home.publish")}
      </button>
      <button type="button" className="btn btn-outline" onClick={() => go("publicaciones", { add: "sheet" })}>
        📄 {t("home.import")}
      </button>
      {gated("reservations", "🤝", t("qa.reserve"), "publicaciones")}
      {gated("coupons", "🎟", t("qa.coupon"), "cupones")}
      <button type="button" className="btn btn-outline" onClick={() => void copy()}>
        🔗 {copied ? t("qa.copied") : t("qa.copy")}
      </button>
      <Link to={`/vendedores/${seller.handle}`} className="btn btn-outline">
        👁 {t("home.seeShop")} ↗
      </Link>
    </section>
  );
}


/** The last 5 sales and the buyers' last 5 messages, each with its button. */
function RecentCard({ orders, messages }: { orders: HomeOrder[]; messages: HomeMessage[] }) {
  const { t, locale } = useI18n();
  const ars = useArs();
  if (orders.length === 0 && messages.length === 0) return null;
  const date = (iso: string) => new Date(iso).toLocaleDateString(locale === "es" ? "es-AR" : "en-US", { day: "numeric", month: "short" });
  return (
    <div className="shop-home-grid">
      {orders.length > 0 && (
        <section className="plan-card shop-home-card" aria-labelledby="home-orders">
          <h2 id="home-orders" className="small-title">
            {t("home.recentOrders")}
          </h2>
          <ul className="home-rows">
            {orders.map((o) => {
              const step = sellerNextStep(o);
              return (
                <li key={o.number}>
                  <span>
                    <strong>{o.buyerName}</strong>{" "}
                    <span className="muted small">
                      · {ars(o.totalArs)} · {date(o.createdAt)}
                    </span>
                  </span>
                  <Link to={orderPath(o.number, "seller")} className={step === "home.next.view" ? "btn btn-link btn-sm" : "btn btn-outline btn-sm"} aria-label={`${t(step)} ${o.number}`}>
                    {t(step)}
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}
      {messages.length > 0 && (
        <section className="plan-card shop-home-card" aria-labelledby="home-messages">
          <h2 id="home-messages" className="small-title">
            {t("home.recentMessages")}
          </h2>
          <ul className="home-rows">
            {messages.map((m) => (
              <li key={`${m.number}-${m.createdAt}`} className={m.unread ? "is-unread" : undefined}>
                <span className="home-message">
                  <span>
                    <strong>{m.buyerName}</strong> <span className="muted small">· {date(m.createdAt)}</span>
                  </span>
                  <span className="small">{m.body}</span>
                </span>
                <Link to={orderPath(m.number, "seller")} className={m.unread ? "btn btn-outline btn-sm" : "btn btn-link btn-sm"} aria-label={`${t(m.unread ? "home.next.reply" : "home.next.view")} ${m.number}`}>
                  {t(m.unread ? "home.next.reply" : "home.next.view")}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** The last 8 weeks' sales as bars, this week last. */
function WeeksChart({ weeks, label, format }: { weeks: number[]; label: string; format: (n: number) => string }) {
  const max = Math.max(0, ...weeks);
  if (max === 0) return null;
  return (
    <div className="weeks-chart" role="img" aria-label={`${label}: ${weeks.map(format).join(", ")}`}>
      {weeks.map((w, i) => (
        <span key={i} title={format(w)} style={{ height: `${Math.max(4, Math.round((w / max) * 100))}%` }} className={i === weeks.length - 1 ? "is-now" : undefined} />
      ))}
    </div>
  );
}

/** "▲ 12%" against the week before; nothing when there's nothing to compare with. */
function Trend({ now, before }: { now: number; before: number }) {
  if (before <= 0 || now === before) return null;
  const pct = Math.round(((now - before) / before) * 100);
  return <span className={pct > 0 ? "trend trend-up" : "trend trend-down"}>{pct > 0 ? `▲ ${pct}%` : `▼ ${-pct}%`}</span>;
}

/** Each task of the guide: its line, why it matters, and its button. */
const GUIDE_TEXT: Record<OnboardingTaskKey, { why: MessageKey; cta: MessageKey }> = {
  photo: { why: "guide.photoWhy", cta: "guide.photoCta" },
  payment: { why: "guide.paymentWhy", cta: "guide.toSettings" },
  delivery: { why: "guide.deliveryWhy", cta: "guide.toSettings" },
  first_listing: { why: "guide.firstListingWhy", cta: "guide.publish" },
  bio: { why: "guide.bioWhy", cta: "guide.bioCta" },
  listings: { why: "guide.listingsWhy", cta: "guide.publish" },
  mp: { why: "guide.mpWhy", cta: "guide.mpCta" },
  verify: { why: "guide.verifyWhy", cta: "guide.verifyCta" },
};

/**
 * "Guía de inicio" (docs/SELLER_ONBOARDING_PLAN.md): from the request to the first sale, one step open
 * at a time (the first one left), each ticked as soon as the shop has it. After approval the shop can
 * close it once nothing required is left; it comes back if something required comes undone. `listings`:
 * every listing prepared under review, the live ones after.
 */
export function SetupGuide({ seller, listings, completedSales, onSaved }: { seller: MySeller; listings: number; completedSales: number; onSaved: (s: MySeller) => void }) {
  const { t } = useI18n();
  const acting = actingShop() !== null;
  const view = onboardingView(seller, { listings, completedSales, acting });
  const [open, setOpen] = useState<OnboardingTaskKey | null>(null);
  const [failed, setFailed] = useState(false);
  const [closing, setClosing] = useState(false);
  if (!view) return null;
  const live = seller.plan?.liveListings ?? 0;
  const label = (task: OnboardingTask) => {
    switch (task.key) {
      case "photo":
        return t("home.reviewPhoto");
      case "payment":
        return t("home.reviewPayment");
      case "delivery":
        return t("home.reviewDelivery");
      case "first_listing":
        return seller.plan?.maxListings ? t("home.reviewListingsCap", { n: seller.plan.maxListings }) : t("home.reviewListings");
      case "bio":
        return t("guide.bio");
      case "listings":
        return t("home.startListings", { live: Math.min(live, ONBOARDING_LISTINGS), max: ONBOARDING_LISTINGS });
      case "mp":
        return t("home.startMp");
      case "verify":
        return t("home.startVerify");
    }
  };
  const expanded = open && view.tasks.some((x) => x.key === open && !x.done) ? open : (view.tasks.find((x) => !x.done)?.key ?? null);
  const share = async () => {
    const url = `${window.location.origin}/vendedores/${seller.handle}`;
    try {
      if (navigator.share) await navigator.share({ title: seller.displayName, url });
      else await navigator.clipboard.writeText(url);
    } catch {
      // Closed the share sheet: nothing to do.
    }
  };
  const dismiss = async () => {
    setFailed(false);
    setClosing(true);
    try {
      await marketApi.dismissOnboarding();
      onSaved({ ...seller, onboardingDismissed: true });
    } catch {
      setFailed(true);
      setClosing(false);
    }
  };
  const pct = Math.round((view.done / view.tasks.length) * 100);
  return (
    <section className="plan-card setup-guide" aria-labelledby="guide-title">
      <div className="setup-guide-head">
        <h2 id="guide-title" className="small-title">
          {t(view.phase === "review" ? "home.reviewTitle" : "home.start")}
        </h2>
        <span className="muted small">{t("guide.progress", { done: view.done, total: view.tasks.length })}</span>
      </div>
      <div className="setup-guide-bar" role="progressbar" aria-label={t("guide.progressLabel")} aria-valuemin={0} aria-valuemax={view.tasks.length} aria-valuenow={view.done}>
        <span style={{ width: `${pct}%` }} />
      </div>
      <ul className="setup-guide-list">
        {view.tasks.map((task) => {
          const text = label(task);
          const tag = task.required ? null : t(task.key === "mp" ? "guide.recommended" : "guide.optional");
          if (task.done)
            return (
              <li key={task.key} className="is-done">
                <span aria-hidden="true">✓</span> <s>{text}</s>
              </li>
            );
          const isOpen = expanded === task.key;
          const { why, cta } = GUIDE_TEXT[task.key];
          return (
            <li key={task.key} className={isOpen ? "is-open" : undefined}>
              <button type="button" className="setup-guide-step" aria-expanded={isOpen} onClick={() => setOpen(task.key)}>
                <span aria-hidden="true">○</span> <span className="setup-guide-name">{text}</span>
                {tag && <span className="muted small">{tag}</span>}
              </button>
              {isOpen && (
                <div className="setup-guide-body">
                  <p className="small">{t(why, { n: ONBOARDING_BIO_CHARS })}</p>
                  {task.ownerOnly && acting ? (
                    <p className="muted small">{t("guide.ownerDoes")}</p>
                  ) : task.to === "#verificacion" ? (
                    <a href="#verificacion" className="btn btn-primary btn-sm">
                      {t(cta)}
                    </a>
                  ) : (
                    <Link to={sellerPath(task.to)} className="btn btn-primary btn-sm">
                      {t(cta)}
                    </Link>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {view.phase === "first_sale" && (
        <p className="setup-guide-foot">
          <button type="button" className="btn btn-link btn-sm" onClick={() => void share()}>
            ↗ {t("home.startShare")}
          </button>
          {view.canDismiss && !acting && (
            <button type="button" className="btn btn-link btn-sm" disabled={closing} onClick={() => void dismiss()}>
              {t("guide.dismiss")}
            </button>
          )}
        </p>
      )}
      {failed && (
        <p className="form-error" role="alert">
          {t("guide.dismissFailed")}
        </p>
      )}
    </section>
  );
}
